/**
 * Between the scheduling UI, which asks for a day and a time separately, and the
 * instruction payload, which carries one ISO timestamp on the meeting branch.
 *
 * The timestamp is built in the SHOOT's timezone, not the client's. A client in
 * Zurich booking a Berlin shoot for 10:00 means 10:00 in Berlin, and composing in
 * the browser's zone is exactly the mistake ISSUE-2945 fixed on the other side of
 * this wire. Everything here therefore takes the catalogue timezone and only
 * falls back to local time when the catalogue has not supplied one.
 */

import moment, { Moment } from 'moment-timezone'

import type { MeetingDTO, MeetingMode } from 'models/purchaseFlowRevamp'

import type { ScheduleMethod } from './scheduleMethod'

/** Earliest and latest hour a shoot may start, inclusive. */
const FIRST_HOUR = 7
const LAST_HOUR = 21

/** Shoots start on the quarter hour, so this is the entire minute vocabulary. */
export const MINUTE_STEPS = [0, 15, 30, 45] as const

export const SHOOTING_HOURS: readonly number[] =
  Array.from({ length: LAST_HOUR - FIRST_HOUR + 1 }, (_, index) => FIRST_HOUR + index)

/** The day, hour and minute the scheduling UI holds. */
export interface ShootingDateTimeParts {
  day: Moment | null
  hour?: number
  minute?: number
}

/** Modes that carry a date. THIRD_PARTY has no time by definition — the creative
 *  arranges it — and NONE has no meeting at all. */
const DATED_MODES: ReadonlySet<MeetingMode> = new Set<MeetingMode>(['ON_SITE', 'KEY_PICKUP'])

export const modeCarriesDate = (mode: MeetingMode | undefined): boolean =>
  mode !== undefined && DATED_MODES.has(mode)

/** Read an ISO timestamp back into the three values the UI shows. */
export const splitDateTime = (iso: string | undefined, timezone?: string): ShootingDateTimeParts => {
  if (!iso) return { day: null }
  const at = timezone ? moment.tz(iso, timezone) : moment(iso)
  if (!at.isValid()) return { day: null }
  return { day: at.clone().startOf('day'), hour: at.hour(), minute: at.minute() }
}

/**
 * The ISO timestamp for a complete pick, or undefined while one is missing.
 *
 * Undefined rather than a partial date on purpose: a day with no time is not a
 * booking, and writing one would put a midnight shoot on the order.
 */
export const composeDateTime = (
  { day, hour, minute }: ShootingDateTimeParts,
  timezone?: string
): string | undefined => {
  if (!day || hour === undefined || minute === undefined) return undefined
  const base = timezone
    ? moment.tz(day.format('YYYY-MM-DD'), 'YYYY-MM-DD', timezone)
    : moment(day.format('YYYY-MM-DD'), 'YYYY-MM-DD')
  const at = base.hour(hour).minute(minute).second(0).millisecond(0)
  return at.isValid() ? at.toISOString() : undefined
}

/**
 * Put the scheduled timestamp on whichever branch the meeting's mode names.
 *
 * Returns the meeting UNCHANGED when there is no dated branch to write to, so a
 * third-party arrangement never acquires a time and the caller does not have to
 * special-case it. The caller keeps the client's pick either way — it is written
 * as soon as a dated mode is chosen.
 *
 * A key pickup under flexible booking is the one dated mode that takes no date:
 * the creative arranges the time, so the pick is dropped and the branch is
 * marked `arrangedByCreative` instead.
 */
export const withScheduledDate = (
  meeting: MeetingDTO | undefined,
  iso: string | undefined,
  method?: ScheduleMethod
): MeetingDTO | undefined => {
  if (!meeting || !modeCarriesDate(meeting.mode)) return meeting
  if (meeting.mode === 'ON_SITE') {
    if (meeting.onSite?.date === iso) return meeting
    return { ...meeting, onSite: { ...meeting.onSite, date: iso } }
  }
  const flexible = method === 'flexible'
  const current = meeting.keyPickup ?? {}
  const date = flexible ? undefined : iso
  if (current.date === date && !!current.arrangedByCreative === flexible) return meeting
  const { date: _date, arrangedByCreative: _flag, ...rest } = current
  return {
    ...meeting,
    keyPickup: { ...rest, ...(date !== undefined && { date }), ...(flexible && { arrangedByCreative: true }) },
  }
}

/** The earliest slot a shoot can start in, read in the shoot's timezone. */
export interface EarliestSlot {
  day: Moment
  hour: number
  minute: number
}

/**
 * The first bookable slot at or after the lead-time floor: rounded up to the next quarter hour, raised to
 * the first shooting hour, and moved to the next morning when the floor day has no slot left.
 */
export const earliestSlot = (floor: Moment | undefined, timezone?: string): EarliestSlot | undefined => {
  if (!floor?.isValid()) return undefined
  const at = (timezone ? floor.clone().tz(timezone) : floor.clone()).second(0).millisecond(0)
  const step = MINUTE_STEPS[1] - MINUTE_STEPS[0]
  const overshoot = at.minute() % step
  if (overshoot) at.add(step - overshoot, 'minutes')
  const lastMinute = MINUTE_STEPS[MINUTE_STEPS.length - 1]
  if (at.hour() > LAST_HOUR || (at.hour() === LAST_HOUR && at.minute() > lastMinute)) at.add(1, 'day').hour(FIRST_HOUR).minute(0)
  else if (at.hour() < FIRST_HOUR) at.hour(FIRST_HOUR).minute(0)
  return { day: at.clone().startOf('day'), hour: at.hour(), minute: at.minute() }
}

/** Whether a slot on `day` starts before the earliest one. Without a minute, whether the whole hour does. */
export const isBeforeEarliestSlot = (
  earliest: EarliestSlot | undefined,
  day: Moment | null | undefined,
  hour: number,
  minute?: number
): boolean => {
  if (!earliest || !day || !day.isSame(earliest.day, 'day')) return false
  if (hour !== earliest.hour) return hour < earliest.hour
  return minute !== undefined && minute < earliest.minute
}
